import { useCallback, useEffect, useState } from 'react';
import { getBalance, getMonthlyHistory } from '../services/apiService';
import { findAccount, findAccountDeep, getMonthRange, sumBRL } from './dashboardUtils';

interface DashboardData {
  receita: number;
  despesas: number;
  lucro: number;
  saldoCaixa: number;
  distribuicaoLucros: number;
  obrigacoesEmAberto: number;
  despesasPorCategoria: { name: string; value: number }[];
  monthlyHistory: { month: string; receita: number; despesas: number }[];
}

export function useDashboardData(year: number, month: number) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { begin, end } = getMonthRange(year, month);

      // 4 parallel requests
      const [monthBalance, totalBalance, historyResponse, obrigacoesBalance] = await Promise.all([
        getBalance({ begin, end }),
        getBalance({ account: 'Ativos', end }),
        getMonthlyHistory({ year, month }),
        getBalance({ account: 'Passivo:Obrigações' }),
      ]);

      const receitas = findAccount(monthBalance.accounts, 'Receitas');
      const despesas = findAccount(monthBalance.accounts, 'Despesas');

      const receitaVal = Math.abs(sumBRL(receitas));
      const despesasVal = sumBRL(despesas);
      const lucro = receitaVal - despesasVal;

      // Saldo em caixa: Ativos líquidos (exclui Veículos, Imobilizado, etc.)
      const EXCLUDE_FROM_CAIXA = ['Imobilizado'];
      let saldoCaixa = 0;
      const ativos = findAccount(totalBalance.accounts, 'Ativos');
      if (ativos) {
        for (const child of ativos.children) {
          if (!EXCLUDE_FROM_CAIXA.includes(child.account)) {
            saldoCaixa += sumBRL(child);
          }
        }
      }

      // Obrigações em aberto (total de Passivo:Obrigações)
      let obrigacoesEmAberto = 0;
      for (const node of obrigacoesBalance.accounts) {
        obrigacoesEmAberto += sumBRL(node);
      }
      obrigacoesEmAberto = Math.abs(obrigacoesEmAberto);

      // Distribuição de lucros
      const distLucros = findAccountDeep(monthBalance.accounts, 'Distribuição de Lucros');
      const distribuicaoLucros = Math.abs(sumBRL(distLucros));

      // Expense breakdown by category
      const despesasPorCategoria: { name: string; value: number }[] = [];
      if (despesas) {
        for (const child of despesas.children) {
          const val = sumBRL(child);
          if (val !== 0) {
            despesasPorCategoria.push({ name: child.account, value: Math.abs(val) });
          }
        }
      }
      despesasPorCategoria.sort((a, b) => b.value - a.value);

      // Map history response to chart format
      const monthlyHistory = historyResponse.months.map((m) => ({
        month: m.label,
        receita: Math.abs(m.receita),
        despesas: m.despesas,
      }));

      setData({
        receita: receitaVal,
        despesas: despesasVal,
        lucro,
        saldoCaixa,
        distribuicaoLucros,
        obrigacoesEmAberto,
        despesasPorCategoria,
        monthlyHistory,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar dados';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
